<div class="legal-wrapper">
  <div class="legal-scroll-content">
    <div class="hero-background"></div>
    <div class="overlay"></div>

    <div class="legal-container">
      <div class="glass-card legal-card">
        
        <div class="legal-header">
          <h1>Terms & Conditions</h1>
          <p class="last-updated">Last updated: April 24, 2026</p>
        </div>

        <div class="legal-body">
          
          <section>
            <h2>1. Acceptance of Terms</h2>
            <p>
              By accessing or using <strong>YourNotes</strong> (the “Service”), operated by 
              <strong>YourNotes</strong> (“we”, “us”, or “our”), you agree to be bound by these Terms & Conditions (“Terms”). 
              If you do not agree to these Terms, you may not access or use the Service. 
              These Terms apply to all users of the Service.
            </p>
          </section>

          <section>
            <h2>2. Description of the Service</h2>
            <p>
              YourNotes provides a digital environment for note‑taking, task management, and calendar synchronization through a unified dashboard. 
              We reserve the right to modify, suspend, or discontinue any part of the Service at any time, including features or content, in accordance with applicable law.
            </p>
          </section>

          <section>
            <h2>3. Privacy and Data Protection</h2>
            <p>
              Your privacy is important to us. Our processing of personal data is governed by our 
              <a href="/privacy">Privacy Policy</a>, which forms an integral part of these Terms. 
              By using the Service, you acknowledge that you have read and understood our Privacy Policy 
              and agree to the processing of personal data as described therein.
            </p>
          </section>

          <section>
            <h2>4. Eligibility</h2>
            <p>
              You must be at least <strong>16 years of age</strong> (or the minimum legal age required in your jurisdiction) to use the Service. 
              By using YourNotes, you declare that you meet this requirement.
            </p>
          </section>

          <section>
            <h2>5. User Accounts and Security</h2>
            <p>To use certain features of the Service, you must create an account. You agree to:</p>
            <ul class="prohibited-list">
              <li>Provide accurate, complete, and current information.</li>
              <li>Keep your login credentials confidential.</li>
              <li>Notify us immediately of any unauthorized use of your account.</li>
            </ul>
            <p>
              You are responsible for all activities that occur under your account. 
              We are not liable for losses caused by unauthorized access resulting from your failure to secure your account.
            </p>
          </section>

          <section>
            <h2>6. Third‑Party Integrations (Calendars)</h2>
            <p>
              The Service allows you to connect third‑party services such as Google Calendar or Microsoft Outlook. 
              By connecting such services, you acknowledge and agree that:
            </p>
            <ul class="prohibited-list">
              <li>You grant us permission to access and display your calendar data as described in our Privacy Policy.</li>
              <li>Calendar data is accessed only to provide the requested functionality.</li>
              <li>Third‑party providers are responsible for their own services and availability.</li>
            </ul>
            <p>We are not responsible for interruptions, errors, or data loss caused by third‑party services.</p>
          </section>

          <section>
            <h2>7. Acceptable Use</h2>
            <p>You agree not to use the Service for any unlawful or prohibited purpose. In particular, you must not:</p>
            <ul class="prohibited-list">
              <li>Violate applicable laws or regulations.</li>
              <li>Infringe intellectual property rights.</li>
              <li>Upload or transmit malicious code, malware, or viruses.</li>
              <li>Use automated systems (scraping, crawling, data harvesting) without prior written consent.</li>
              <li>Interfere with the security or proper functioning of the Service.</li>
            </ul>
            <p>Violation of this section may result in immediate suspension or termination of your account.</p>
          </section>

          <section>
            <h2>8. Intellectual Property</h2>
            <p>
              The Service and all original content, features, and functionality are owned by YourNotes or its licensors 
              and are protected by intellectual property laws. You may not copy, modify, distribute, or exploit 
              any part of the Service without prior written permission, except as permitted by law.
            </p>
          </section>

          <section>
            <h2>9. User Content</h2>
            <p>
              You retain ownership of all notes, tasks, attachments, meeting notes, and other content that you create, upload, store, or share through the Service (“User Content”).
            </p>
            <p>
              By using the Service, you grant YourNotes a limited, non‑exclusive licence to host, store, process, transmit, and display User Content solely for the purpose of providing, maintaining, securing, and improving the Service.
            </p>
            <p>
              You are solely responsible for your User Content and for ensuring that you have the necessary rights to create, upload, store, or share such content.
            </p>
          </section>

          <section>
            <h2>10. Subscription and Payment</h2>
            <p>Certain features of the Service may require a paid subscription.</p>
            <ul class="prohibited-list">
              <li>Subscription fees, billing periods, and payment terms will be communicated before purchase.</li>
              <li>Unless otherwise stated, subscriptions renew automatically until cancelled.</li>
              <li>You may cancel your subscription at any time. Cancellation will take effect at the end of the current billing period.</li>
              <li>Except where required by applicable law, payments already made are non‑refundable.</li>
            </ul>
          </section>

          <section>
            <h2>11. Availability and No Warranty</h2>
            <p>The Service is provided on an “as is” and “as available” basis. To the maximum extent permitted by law, we make no warranties or guarantees that:</p>
            <ul class="prohibited-list">
              <li>The Service will be uninterrupted or error‑free.</li>
              <li>Data will always be available or preserved.</li>
              <li>The Service will meet your specific requirements.</li>
            </ul>
          </section>

          <section>
            <h2>12. Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by applicable law, YourNotes shall not be liable for any indirect, incidental, 
              consequential, or special damages, including but not limited to loss of data, revenue, or profits, arising from 
              use or inability to use the Service. Nothing in these Terms excludes liability that cannot be excluded 
              under <strong>Dutch or EU law</strong>.
            </p>
          </section>

          <section>
            <h2>13. Termination</h2>
            <p>
              We may suspend or terminate your account if you breach these Terms or misuse the Service. 
              You may terminate your account at any time via your account settings. Upon termination, 
              access to the Service will cease, and your data will be handled in accordance with our Privacy Policy.
            </p>
          </section>

          <section>
            <h2>14. Changes to the Terms</h2>
            <p>
              We reserve the right to update these Terms from time to time. Changes will be posted on this page, 
              and the “Last updated” date will be adjusted. Continued use of the Service after changes constitutes 
              acceptance of the revised Terms.
            </p>
          </section>

          <section>
            <h2>15. Consumer Rights</h2>
            <p>
              If you are a consumer residing within the European Union, nothing in these Terms limits or excludes any mandatory rights available under applicable consumer protection laws.
            </p>
            <p>
              Where applicable, by requesting immediate access to digital services, you acknowledge that certain statutory withdrawal rights may be lost once the Service has commenced, to the extent permitted by law.
            </p>
          </section>

          <section>
            <h2>16. Governing Law and Jurisdiction</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of <strong>The Netherlands</strong>. 
              Any disputes shall be subject to the exclusive jurisdiction of the competent courts of The Netherlands.
            </p>
          </section>

          <section>
            <h2>17. Severability</h2>
            <p>
              If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions 
              will remain in full force and effect.
            </p>
          </section>

          <section class="contact-section">
            <h2>18. Contact</h2>
            <p>If you have any questions about these Terms, please contact us at:</p>
            <a href="mailto:info@yournotes.nl" class="email-link">info [at] yournotes.nl</a>
          </section>

        </div>
      </div>
    </div>
  </div>

  <mat-toolbar class="bottom-toolbar">
    <button mat-flat-button (click)="back()">
      <mat-icon>arrow_back</mat-icon> Back
    </button>
  </mat-toolbar>
</div>